import { Transaction } from '../types/Transaction';

/**
 * As transações que aparecem num mês da projeção.
 *
 * Uma parcelada aparece em vários meses a partir de UM registro: guardamos a
 * compra uma vez, com o número da parcela que ela informou, e cada mês da
 * visão mostra a parcela que cai ali.
 *
 * **O parcelamento começa na parcela informada.** "parcela 3/6 em outubro" cai
 * em outubro, novembro, dezembro e janeiro — quatro, as que ainda faltam. Não
 * reconstrói agosto e setembro: informar uma parcela em andamento é dizer
 * "daqui pra frente ainda saem N", não inventar gasto em meses que ela nunca
 * lançou. A mesma regra vive em `projectionCalc.installmentNumberIn` no
 * backend, e as duas precisam continuar iguais.
 *
 * ── O que este arquivo aprendeu do jeito caro ──────────────────────────────
 *
 * A versão anterior devolvia `{ ...t, currentInstallment: n }` — sobrescrevia
 * o dado com o número derivado da tela. Como a lista alimenta o formulário de
 * edição, e o formulário grava o que recebeu, editar uma parcelada olhando
 * outro mês **regravava a parcela errada no banco**. Uma compra 3/6 vista em
 * agosto virava 1/6 permanentemente, e o número "voltava para 1" sozinho.
 *
 * Por isso a parcela do mês vai em `displayInstallment`, um campo SÓ de
 * exibição, e `currentInstallment` chega intacto ao formulário. Derivado e
 * armazenado não podem morar no mesmo campo quando um deles volta para o banco.
 */

/** Quantos meses informados entram na média — o mesmo N do backend. */
export const MESES_NA_MEDIA = 3;

const MES_VALIDO = /^\d{4}-(0[1-9]|1[0-2])$/;

const anoMes = (dateStr: string): { year: number; month: number } | null => {
  const p = dateStr.split('T')[0].split('-').map(Number);
  if (p.length < 2 || !Number.isFinite(p[0]) || !Number.isFinite(p[1])) return null;
  return { year: p[0], month: p[1] - 1 };
};

/** A mensal está vigente no mês 'AAAA-MM'? Espelha `projectionCalc.isMonthlyActiveIn`. */
const mensalAtivaEm = (t: Transaction, monthKey: string): boolean => {
  const ym = anoMes(t.date);
  if (!ym) return false;
  const [y, m] = monthKey.split('-').map(Number);
  const proj = y * 12 + (m - 1);
  if (ym.year * 12 + ym.month > proj) return false;
  if (t.endDate) {
    const fim = anoMes(t.endDate);
    if (fim && fim.year * 12 + fim.month < proj) return false;
  }
  return true;
};

/**
 * Quanto uma MENSAL pesa no mês 'AAAA-MM' — espelho de
 * `projectionCalc.valorDaMensalNoMes` (15/09), preso a ele por
 * `shared/valorDoMes.casos.json`.
 *
 * Informado no mês → ele. Senão, média dos últimos `MESES_NA_MEDIA`
 * informados ANTES do mês, na mesma era de `valueSince`. Nenhum → o cadastrado.
 * O porquê de cada escolha está no backend.
 */
export const valorNoMes = (
  t: Transaction,
  monthKey: string,
): { valor: number; origem: 'informado' | 'media' | 'cadastrado' } => {
  if (!mensalAtivaEm(t, monthKey)) return { valor: 0, origem: 'cadastrado' };

  const informados = t.monthValues ?? {};
  const noMes = informados[monthKey];
  if (typeof noMes === 'number' && Number.isFinite(noMes)) return { valor: noMes, origem: 'informado' };

  const desde = t.valueSince && MES_VALIDO.test(t.valueSince) ? t.valueSince : null;
  const naMesmaEra = (mes: string) => !desde || (monthKey >= desde ? mes >= desde : mes < desde);

  const anteriores = Object.entries(informados)
    .filter(([mes, v]) => MES_VALIDO.test(mes) && mes < monthKey && naMesmaEra(mes)
      && typeof v === 'number' && Number.isFinite(v) && mensalAtivaEm(t, mes))
    .sort(([a], [b]) => (a < b ? 1 : -1))
    .slice(0, MESES_NA_MEDIA)
    .map(([, v]) => v);

  if (anteriores.length === 0) return { valor: t.value, origem: 'cadastrado' };
  const media = anteriores.reduce((s, v) => s + v, 0) / anteriores.length;
  return { valor: Math.round(media * 100) / 100, origem: 'media' };
};

/**
 * O valor que a TELA soma e mostra: o do mês quando existe, o gravado senão.
 *
 * Toda soma sobre a lista de um mês passa por aqui. Somar `t.value` direto
 * mostraria o cadastrado de uma mensal cujo valor do mês é outro — a linha
 * diria uma coisa e o total, outra.
 */
export const valorExibido = (t: Transaction): number => t.displayValue ?? t.value;

export function transactionsInMonth(transactions: Transaction[], monthKey: string): Transaction[] {
  const [yearStr, monthStr] = monthKey.split('-');
  const projYear = parseInt(yearStr);
  const projMonth = parseInt(monthStr) - 1;
  const result: Transaction[] = [];

  transactions.forEach(t => {
    const ym = anoMes(t.date);
    if (!ym) return;
    const { year: tYear, month: tMonth } = ym;

    if (t.isMonthly) {
      // Espelha `projectionCalc.isMonthlyActiveIn`: do mês em que nasceu até o
      // mês de `endDate`, inclusive. Sem olhar o fim, uma assinatura cancelada
      // seguia na lista e nos totais do mês enquanto o saldo já não a contava.
      const proj = projYear * 12 + projMonth;
      if (tYear * 12 + tMonth > proj) return; // ainda não começou
      if (t.endDate) {
        const fim = anoMes(t.endDate);
        if (fim && fim.year * 12 + fim.month < proj) return; // já terminou
      }
      // O valor do mês vai num campo SÓ de exibição; `value` fica intacto
      // (armadilha 5b — a mesma lição de `displayInstallment` acima).
      const { valor, origem } = valorNoMes(t, monthKey);
      result.push({ ...t, displayValue: valor, displayValueOrigem: origem });
    } else if (t.hasInstallments && t.installments) {
      // Fora da faixa, o valor gravado não é confiável: trata como a primeira.
      const atual = Math.min(Math.max(t.currentInstallment ?? 1, 1), t.installments);
      const diff = (projYear - tYear) * 12 + (projMonth - tMonth);
      const restantes = t.installments - atual + 1;
      if (diff >= 0 && diff < restantes) {
        // `currentInstallment` fica como está: é o dado dela.
        result.push({ ...t, displayInstallment: atual + diff });
      }
    } else {
      if (tYear === projYear && tMonth === projMonth) result.push(t);
    }
  });

  return result;
}
